/**
 * @fileoverview EscalationsCard component for displaying member escalations
 * @module components/cards/EscalationsCard
 * @description Renders the Escalations tab as a table (mirrors
 * ClinicalProgramsTable's look and feel): Escalation Type, Reason, Priority,
 * Status, Owner, Created By, Created On, plus a trailing unlabeled action
 * column. Rows themselves aren't clickable - only that column's history icon
 * opens the escalation's version history via onSelectEscalation.
 */

import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";

import formatDateTimeUtil from "../../utils/formatDateTime";

const DEFAULT_CHIP_COLORS = {
  bg: "#ECECF1",
  border: "#ECECF1",
  text: "#403C6F",
};

const TYPE_CHIP_COLORS = {
  "Patient Safety Concern": {
    bg: "#FDECEC",
    border: "#F5C2C2",
    text: "#D32F2F",
  },
  "Clinical Concern": { bg: "#FDF6E3", border: "#F0DDA0", text: "#B45309" },
  "Medication Issue": { bg: "#FDF6E3", border: "#F0DDA0", text: "#B45309" },
};

const STATUS_CHIP_COLORS = {
  Open: { bg: "#ECECF1", text: "#403C6F" },
  Resolved: { bg: "#E5F7EF", text: "#1B7A43" },
  Canceled: { bg: "#FDECEC", text: "#D32F2F" },
};

const HEADERS = [
  { label: "Escalation Type", width: "16%" },
  { label: "Escalation Reason", width: "18%" },
  { label: "Priority", width: "10%" },
  { label: "Status", width: "10%" },
  { label: "Owner", width: "14%" },
  { label: "Created By", width: "14%" },
  { label: "Created On", width: "14%" },
  // No header label - this is the row's only action (open the escalation's
  // version history), not a data column.
  { label: "", width: "4%" },
];

const CELL_BORDER = {
  borderColor: "#E8E8E9",
  borderWidth: "0px 0.5px 0px 0.5px",
  borderStyle: "solid",
};

const Chip = ({ label, bg, text, border }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      bgcolor: bg,
      borderRadius: "16px",
      p: "4px 10px",
      border: border ? `1px solid ${border}` : "none",
      boxSizing: "border-box",
    }}
  >
    <Typography
      sx={{
        color: text,
        fontWeight: 600,
        fontSize: "11px",
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
  </Box>
);

Chip.propTypes = {
  label: PropTypes.string.isRequired,
  bg: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  border: PropTypes.string,
};

const EscalationsCard = ({ escalations = [], onSelectEscalation }) => {
  const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <Box
      sx={{
        mt: 1,
        flex: 1,
        minHeight: 0,
        height: "100%",
        pb: 3,
        borderRadius: 2,
        backgroundColor: "#FFFFFF",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TableContainer
        sx={{
          flex: 1,
          minHeight: 0,
          borderRadius: "8px",
          border: "1px solid #E8E8E9",
          overflowY: "auto",
          overflowX: "auto",
          "&::-webkit-scrollbar": {
            width: "6px",
            height: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#F5F5F5",
            borderRadius: "4px",
            margin: "4px 0",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#A7A5BD",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#8E8CA8",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "#A7A5BD #F5F5F5",
        }}
      >
        <Table stickyHeader size="small" sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#CFCEDA",
                position: "sticky",
                top: 0,
                zIndex: 5000,
                boxShadow: "0 2px 0 rgba(0,0,0,0.05)",
              }}
            >
              {HEADERS.map((header) => (
                <TableCell
                  key={header.label}
                  sx={{
                    backgroundColor: "#CFCEDA",
                    color: "#403C6F",
                    fontWeight: 700,
                    fontSize: "14px",
                    py: 1.5,
                    borderBottom: "none",
                    whiteSpace: "nowrap",
                    width: header.width,
                    position: "sticky",
                    top: 0,
                    zIndex: 5000,
                    borderColor: "#f5f5f8ff",
                    borderWidth: "0px 1px 0px 1px",
                    borderStyle: "solid",
                  }}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {escalations.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={HEADERS.length}
                  align="center"
                  sx={{
                    py: 10,
                    color: "#66638C",
                    fontSize: "14px",
                    borderBottom: "none",
                  }}
                >
                  No Escalations available
                </TableCell>
              </TableRow>
            ) : (
              escalations.map((escalation, index) => {
                const displayDateTime = formatDateTimeUtil(
                  escalation.createdAt,
                  { timeZone: systemTimeZone },
                );
                const chipColors =
                  TYPE_CHIP_COLORS[escalation.escalationType] ||
                  DEFAULT_CHIP_COLORS;
                const statusColors =
                  STATUS_CHIP_COLORS[escalation.status] || DEFAULT_CHIP_COLORS;
                const isUrgent = escalation.priority === "Urgent";

                return (
                  <TableRow
                    key={escalation.id || index}
                    sx={{
                      "&:hover": { backgroundColor: "#F7F8FB" },
                      "& td": { borderBottom: "1px solid #E0E0E0" },
                    }}
                  >
                    <TableCell
                      sx={{ py: 1.5, verticalAlign: "middle", ...CELL_BORDER }}
                    >
                      <Chip
                        label={escalation.escalationType || "N/A"}
                        bg={chipColors.bg}
                        text={chipColors.text}
                        border={chipColors.border}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        verticalAlign: "middle",
                        color: "#1B2F3F",
                        fontWeight: 500,
                        fontSize: "14px",
                        wordBreak: "break-word",
                        ...CELL_BORDER,
                      }}
                    >
                      {escalation.escalationReason || "N/A"}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        verticalAlign: "middle",
                        fontWeight: isUrgent ? 700 : 500,
                        fontSize: "14px",
                        color: isUrgent ? "#D32F2F" : "#1B2F3F",
                        ...CELL_BORDER,
                      }}
                    >
                      {escalation.priority || "N/A"}
                    </TableCell>
                    <TableCell
                      sx={{ py: 1.5, verticalAlign: "middle", ...CELL_BORDER }}
                    >
                      <Chip
                        label={escalation.status || "N/A"}
                        bg={statusColors.bg}
                        text={statusColors.text}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        verticalAlign: "middle",
                        color: "#1B2F3F",
                        fontWeight: 500,
                        fontSize: "14px",
                        wordBreak: "break-word",
                        ...CELL_BORDER,
                      }}
                    >
                      {escalation.ownerName || "Unassigned"}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        verticalAlign: "middle",
                        color: "#403C6F",
                        fontWeight: 600,
                        fontSize: "14px",
                        wordBreak: "break-word",
                        ...CELL_BORDER,
                      }}
                    >
                      {escalation.createdBy || "Unknown"}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        verticalAlign: "middle",
                        color: "#1B2F3F",
                        fontWeight: 500,
                        fontSize: "14px",
                        whiteSpace: "nowrap",
                        ...CELL_BORDER,
                      }}
                    >
                      {displayDateTime || "N/A"}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ py: 1.5, verticalAlign: "middle", ...CELL_BORDER }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => onSelectEscalation?.(escalation)}
                        aria-label="View escalation history"
                        sx={{
                          color: "#403C6F",
                          "&:hover": { backgroundColor: "#ECECF1" },
                        }}
                      >
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

EscalationsCard.propTypes = {
  escalations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      escalationType: PropTypes.string,
      escalationReason: PropTypes.string,
      description: PropTypes.string,
      followup: PropTypes.string,
      status: PropTypes.string,
      priority: PropTypes.string,
      ownerName: PropTypes.string,
      followUpDate: PropTypes.string,
      resolution: PropTypes.string,
      createdBy: PropTypes.string,
      createdAt: PropTypes.string,
      updatedBy: PropTypes.string,
      updatedAt: PropTypes.string,
      assigneeName: PropTypes.string,
      resolutionBy: PropTypes.string,
      resolutionDatetime: PropTypes.string,
    }),
  ),
  onSelectEscalation: PropTypes.func,
};

export default EscalationsCard;
